<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  contact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'updated']);

const store = useStore();
const route = useRoute();
const isSaving = ref(false);
const activeTab = ref('geral');
const showImageLightbox = ref(false);

const formState = reactive({
  name: '',
  email: '',
  phoneNumber: '',
  companyName: '',
  city: '',
  description: '',
  cpf: '',
  physicalFolder: '',
  googleDriveLink: '',
  inssPassword: '',
  isClient: false,
  signedContract: false,
  customAttributes: {},
});

const contactProfileLink = computed(() => {
  const accountId = route.params.accountId || store.getters.getCurrentAccountId;
  return `/app/accounts/${accountId}/contacts/${props.contact?.id}`;
});

const allContactAttributes = computed(() => {
  const all = store.getters['attributes/getContactAttributes'] || [];
  return all.filter(
    attr => !['is_client', 'signed_contract', 'n_cpf', 'cpf', 'pasta_fisica', 'pasta_drive', 'senha_inss'].includes(attr.attributeKey)
  );
});

const contactProcesses = computed(() => {
  return props.contact?.additional_attributes?.processes || props.contact?.additionalAttributes?.processes || [];
});

const getStatusBadgeClass = (status) => {
  const s = (status || '').toLowerCase().trim();
  if (s.includes('andamento')) {
    return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30';
  }
  if (s.includes('ganho') || s.includes('pago') || s.includes('conclu')) {
    return 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30';
  }
  if (s.includes('recurso') || s.includes('viabilidade') || s.includes('pendente')) {
    return 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30';
  }
  if (s.includes('perdido') || s.includes('cancelado') || s.includes('arquivado')) {
    return 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border border-rose-500/30';
  }
  return 'bg-slate-500/15 text-slate-600 dark:text-slate-400 border border-slate-500/30';
};

const syncFromContact = () => {
  if (!props.contact) return;
  const c = props.contact;
  const addAttr = c.additional_attributes || c.additionalAttributes || {};
  const custAttr = c.custom_attributes || c.customAttributes || {};

  formState.name = c.name || '';
  formState.email = c.email || '';
  formState.phoneNumber = c.phone_number || c.phoneNumber || '';
  formState.companyName = addAttr.company_name || addAttr.companyName || '';
  formState.city = addAttr.city || '';
  formState.description = addAttr.description || '';
  formState.cpf = addAttr.cpf || custAttr.n_cpf || custAttr.cpf || '';
  formState.physicalFolder = addAttr.physical_folder || custAttr.pasta_fisica || '';
  formState.googleDriveLink = addAttr.google_drive_link || custAttr.pasta_drive || '';
  formState.inssPassword = addAttr.inss_password || custAttr.senha_inss || '';
  
  formState.isClient = (
    custAttr.is_client === true ||
    custAttr.is_client === 'true' ||
    addAttr.is_client === true ||
    addAttr.is_client === 'true'
  );
  
  formState.signedContract = (
    custAttr.signed_contract === true ||
    custAttr.signed_contract === 'true' ||
    addAttr.signed_contract === true ||
    addAttr.signed_contract === 'true'
  );

  formState.customAttributes = { ...custAttr };
};

watch(
  () => [props.show, props.contact],
  ([newShow]) => {
    if (newShow) {
      syncFromContact();
      activeTab.value = 'geral';
      showImageLightbox.value = false;
      store.dispatch('attributes/get');
    }
  },
  { immediate: true }
);

const close = () => {
  showImageLightbox.value = false;
  emit('close');
};

const openImageLightbox = () => {
  showImageLightbox.value = true;
};

const closeImageLightbox = () => {
  showImageLightbox.value = false;
};

const openExternalLink = (url) => {
  if (!url) return;
  let fullUrl = url.trim();
  if (!/^https?:\/\//i.test(fullUrl)) {
    fullUrl = `https://${fullUrl}`;
  }
  window.open(fullUrl, '_blank', 'noopener,noreferrer');
};

const formatE164Phone = (rawPhone) => {
  if (!rawPhone) return '';
  let phone = String(rawPhone).trim();
  if (!phone) return '';
  const hasPlus = phone.startsWith('+');
  const digits = phone.replace(/\D/g, '');
  if (!digits) return '';
  if (hasPlus) return `+${digits}`;
  if (digits.startsWith('55') && digits.length >= 12) {
    return `+${digits}`;
  }
  if (digits.length === 10 || digits.length === 11) {
    return `+55${digits}`;
  }
  return `+${digits}`;
};

const saveContact = async () => {
  if (!formState.name || !formState.name.trim()) {
    useAlert('O nome do contato é obrigatório.');
    return;
  }

  isSaving.value = true;
  try {
    const existingAddAttr = props.contact.additional_attributes || props.contact.additionalAttributes || {};
    const existingCustAttr = props.contact.custom_attributes || props.contact.customAttributes || {};

    const updatedAddAttr = {
      ...existingAddAttr,
      company_name: formState.companyName,
      city: formState.city,
      description: formState.description,
      cpf: formState.cpf,
      pasta_fisica: formState.physicalFolder,
      physical_folder: formState.physicalFolder,
      link_drive: formState.googleDriveLink,
      google_drive_link: formState.googleDriveLink,
      senha_inss: formState.inssPassword,
      inss_password: formState.inssPassword,
      cliente: formState.isClient,
      is_client: formState.isClient,
      signed_contract: formState.signedContract,
    };

    const updatedCustAttr = {
      ...existingCustAttr,
      ...formState.customAttributes,
      is_client: formState.isClient,
      signed_contract: formState.signedContract,
      n_cpf: formState.cpf,
      cpf: formState.cpf,
      pasta_fisica: formState.physicalFolder,
      pasta_drive: formState.googleDriveLink,
      senha_inss: formState.inssPassword,
    };

    let formattedPhone = formState.phoneNumber ? formatE164Phone(formState.phoneNumber) : null;
    let formattedEmail = formState.email && formState.email.trim() ? formState.email.trim() : null;

    const payload = {
      id: props.contact.id,
      name: formState.name.trim(),
      email: formattedEmail,
      phone_number: formattedPhone,
      additional_attributes: updatedAddAttr,
      customAttributes: updatedCustAttr,
    };

    await store.dispatch('contacts/update', payload);

    try {
      await store.dispatch('contacts/show', { id: props.contact.id });
      await store.dispatch('contacts/fetchContactableInbox', props.contact.id);
    } catch (fetchErr) {
      console.warn('Post-update fetch warning:', fetchErr);
    }

    useAlert('Contato atualizado com sucesso!');
    emit('updated');
    close();
  } catch (error) {
    console.error('Erro ao salvar contato:', error);
    let errMsg = 'Erro ao salvar alterações do contato.';
    if (error?.response?.data?.message) {
      errMsg = error.response.data.message;
    } else if (error?.response?.data?.attributes?.length) {
      errMsg = `Erro nos campos: ${error.response.data.attributes.join(', ')}`;
    } else if (error?.message) {
      errMsg = error.message;
    }
    useAlert(errMsg);
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <Teleport to="body">
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="show"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-5 bg-slate-950/60 backdrop-blur-md"
        @click.self="close"
        @keydown.esc="close"
      >
        <!-- Modal Card com Glassmorphism Moderno -->
        <div
          class="relative flex flex-col w-full max-w-2xl max-h-[90vh] rounded-2xl bg-white/90 dark:bg-slate-900/90 backdrop-blur-2xl border border-white/40 dark:border-slate-700/60 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] text-slate-900 dark:text-slate-100 overflow-hidden"
          @click.stop
        >
          <!-- Header Glass -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200/60 dark:border-slate-800/80 bg-white/40 dark:bg-slate-800/40">
            <div class="flex items-center gap-4 min-w-0">
              <!-- Avatar com Hover Interativo para Ampliar -->
              <div
                class="relative group shrink-0 cursor-pointer rounded-2xl overflow-hidden shadow-md border-2 border-white/80 dark:border-slate-700 transition-all hover:scale-105 hover:shadow-lg"
                title="Clique para ampliar a foto"
                @click="openImageLightbox"
              >
                <Avatar
                  :src="contact.thumbnail"
                  :name="contact.name"
                  :size="60"
                  hide-offline-status
                  class="!rounded-2xl shrink-0"
                />
                <div class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white backdrop-blur-[1px]">
                  <span class="i-lucide-maximize-2 size-4 mb-0.5" />
                  <span class="text-[9px] font-bold uppercase tracking-wider">Ver</span>
                </div>
              </div>

              <div class="flex flex-col min-w-0">
                <div class="flex items-center gap-2">
                  <h2 class="text-base sm:text-lg font-bold truncate text-slate-900 dark:text-slate-100">
                    {{ contact.name || 'Contato' }}
                  </h2>
                  <span
                    v-if="formState.isClient"
                    class="px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30"
                  >
                    Cliente
                  </span>
                  <span
                    v-if="formState.signedContract"
                    class="px-2 py-0.5 text-[10px] font-bold uppercase rounded-full border"
                    :style="{
                      backgroundColor: 'rgba(var(--color-primary-rgb, 39, 120, 226), 0.15)',
                      color: 'var(--color-primary, #2778e2)',
                      borderColor: 'rgba(var(--color-primary-rgb, 39, 120, 226), 0.3)',
                    }"
                  >
                    Contrato
                  </span>
                </div>
                <div class="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                  <span v-if="contact.email" class="truncate">{{ contact.email }}</span>
                  <span v-if="contact.email && contact.phone_number">•</span>
                  <span v-if="contact.phone_number" class="font-mono">{{ contact.phone_number }}</span>
                  <span>• ID #{{ contact.id }}</span>
                </div>
              </div>
            </div>

            <!-- Botões Header: Link externo /contacts/:id e Fechar -->
            <div class="flex items-center gap-1.5 shrink-0">
              <a
                :href="contactProfileLink"
                target="_blank"
                rel="noopener nofollow noreferrer"
                class="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-100/80 dark:bg-slate-800/80 rounded-xl border border-slate-200/80 dark:border-slate-700/80 transition-all cursor-pointer shadow-xs"
                title="Abrir página completa do contato em nova aba"
              >
                <span>/contacts</span>
                <span class="i-lucide-external-link size-3.5" />
              </a>

              <button
                type="button"
                class="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
                title="Fechar (Esc)"
                @click="close"
              >
                <span class="i-lucide-x size-5" />
              </button>
            </div>
          </div>

          <!-- Navegação por Abas Glass -->
          <div class="flex items-center gap-1 px-6 pt-2 border-b border-slate-200/60 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-900/50">
            <button
              type="button"
              class="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold border-b-2 transition-all cursor-pointer"
              :class="activeTab === 'geral' ? 'border-[var(--color-primary,#2778e2)] text-[var(--color-primary,#2778e2)] bg-slate-200/30 dark:bg-slate-800/40 rounded-t-lg' : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'"
              @click="activeTab = 'geral'"
            >
              <span class="i-lucide-user size-3.5" />
              <span>Geral</span>
            </button>

            <button
              type="button"
              class="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold border-b-2 transition-all cursor-pointer"
              :class="activeTab === 'juridico' ? 'border-[var(--color-primary,#2778e2)] text-[var(--color-primary,#2778e2)] bg-slate-200/30 dark:bg-slate-800/40 rounded-t-lg' : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'"
              @click="activeTab = 'juridico'"
            >
              <span class="i-lucide-shield-check size-3.5" />
              <span>Jurídico & Acessos</span>
            </button>

            <button
              type="button"
              class="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold border-b-2 transition-all cursor-pointer"
              :class="activeTab === 'atributos' ? 'border-[var(--color-primary,#2778e2)] text-[var(--color-primary,#2778e2)] bg-slate-200/30 dark:bg-slate-800/40 rounded-t-lg' : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'"
              @click="activeTab = 'atributos'"
            >
              <span class="i-lucide-sliders size-3.5" />
              <span>Atributos da Conta ({{ allContactAttributes.length }})</span>
            </button>

            <button
              v-if="contactProcesses.length"
              type="button"
              class="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold border-b-2 transition-all cursor-pointer"
              :class="activeTab === 'processos' ? 'border-[var(--color-primary,#2778e2)] text-[var(--color-primary,#2778e2)] bg-slate-200/30 dark:bg-slate-800/40 rounded-t-lg' : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'"
              @click="activeTab = 'processos'"
            >
              <span class="i-lucide-scale size-3.5" />
              <span>Processos ({{ contactProcesses.length }})</span>
            </button>
          </div>

          <!-- Corpo do Formulário com Scroll Suave -->
          <div class="flex-1 overflow-y-auto p-6 space-y-4">
            
            <!-- ABA 1: GERAL -->
            <div v-show="activeTab === 'geral'" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="sm:col-span-2">
                <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Nome Completo *
                </label>
                <input
                  v-model="formState.name"
                  type="text"
                  placeholder="Nome do contato"
                  class="w-full h-9 px-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 outline-none focus:border-[var(--color-primary,#2778e2)] focus:ring-2 focus:ring-[var(--color-primary,#2778e2)]/20 transition-all"
                />
              </div>

              <div>
                <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  E-mail
                </label>
                <input
                  v-model="formState.email"
                  type="email"
                  placeholder="email@dominio.com"
                  class="w-full h-9 px-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 outline-none focus:border-[var(--color-primary,#2778e2)] focus:ring-2 focus:ring-[var(--color-primary,#2778e2)]/20 transition-all"
                />
              </div>

              <div>
                <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Telefone / WhatsApp
                </label>
                <input
                  v-model="formState.phoneNumber"
                  type="text"
                  placeholder="+5517999999999"
                  class="w-full h-9 px-3 text-sm font-mono rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 outline-none focus:border-[var(--color-primary,#2778e2)] focus:ring-2 focus:ring-[var(--color-primary,#2778e2)]/20 transition-all"
                />
              </div>

              <div>
                <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Cidade / Estado
                </label>
                <input
                  v-model="formState.city"
                  type="text"
                  placeholder="Ex: São Paulo, SP"
                  class="w-full h-9 px-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 outline-none focus:border-[var(--color-primary,#2778e2)] focus:ring-2 focus:ring-[var(--color-primary,#2778e2)]/20 transition-all"
                />
              </div>

              <div>
                <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Empresa / Razão Social
                </label>
                <input
                  v-model="formState.companyName"
                  type="text"
                  placeholder="Ex: Empresa Ltda"
                  class="w-full h-9 px-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 outline-none focus:border-[var(--color-primary,#2778e2)] focus:ring-2 focus:ring-[var(--color-primary,#2778e2)]/20 transition-all"
                />
              </div>

              <div class="sm:col-span-2">
                <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Descrição / Notas Rápidas
                </label>
                <textarea
                  v-model="formState.description"
                  rows="2"
                  placeholder="Observações importantes sobre o cliente..."
                  class="w-full p-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 outline-none focus:border-[var(--color-primary,#2778e2)] focus:ring-2 focus:ring-[var(--color-primary,#2778e2)]/20 transition-all resize-none"
                />
              </div>
            </div>

            <!-- ABA 2: JURÍDICO & ACESSOS -->
            <div v-show="activeTab === 'juridico'" class="space-y-4">
              <!-- Switchers de Status -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3.5 rounded-xl bg-slate-100/70 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60">
                <label class="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    v-model="formState.isClient"
                    type="checkbox"
                    class="size-4.5 rounded dark:bg-slate-900 dark:border-slate-700"
                    :style="{ accentColor: 'var(--color-primary, #2778e2)' }"
                  />
                  <div>
                    <span class="text-xs font-bold text-slate-800 dark:text-slate-200 block">É Cliente Ativo?</span>
                    <span class="text-[11px] text-slate-500 dark:text-slate-400">Marca o contato como cliente oficial</span>
                  </div>
                </label>

                <label class="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    v-model="formState.signedContract"
                    type="checkbox"
                    class="size-4.5 rounded dark:bg-slate-900 dark:border-slate-700"
                    :style="{ accentColor: 'var(--color-primary, #2778e2)' }"
                  />
                  <div>
                    <span class="text-xs font-bold text-slate-800 dark:text-slate-200 block">Contrato Assinado?</span>
                    <span class="text-[11px] text-slate-500 dark:text-slate-400">Confirma contrato/procuração assinado</span>
                  </div>
                </label>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                    CPF (Cadastro de Pessoa Física)
                  </label>
                  <div class="relative">
                    <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400 pointer-events-none">
                      <span class="i-lucide-id-card size-4" />
                    </span>
                    <input
                      v-model="formState.cpf"
                      type="text"
                      placeholder="000.000.000-00"
                      class="w-full h-9 pl-9 pr-3 text-sm font-mono rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 outline-none focus:border-[var(--color-primary,#2778e2)] focus:ring-2 focus:ring-[var(--color-primary,#2778e2)]/20 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                    Número da Pasta Física
                  </label>
                  <div class="relative">
                    <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400 pointer-events-none">
                      <span class="i-lucide-folder size-4" />
                    </span>
                    <input
                      v-model="formState.physicalFolder"
                      type="text"
                      placeholder="Ex: Pasta 42 / Gaveta B"
                      class="w-full h-9 pl-9 pr-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 outline-none focus:border-[var(--color-primary,#2778e2)] focus:ring-2 focus:ring-[var(--color-primary,#2778e2)]/20 transition-all"
                    />
                  </div>
                </div>

                <div class="sm:col-span-2">
                  <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                    Link do Google Drive (Documentos)
                  </label>
                  <div class="flex items-center gap-2">
                    <div class="relative flex-1">
                      <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400 pointer-events-none">
                        <span class="i-lucide-link size-4" />
                      </span>
                      <input
                        v-model="formState.googleDriveLink"
                        type="url"
                        placeholder="https://drive.google.com/drive/folders/..."
                        class="w-full h-9 pl-9 pr-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 outline-none focus:border-[var(--color-primary,#2778e2)] focus:ring-2 focus:ring-[var(--color-primary,#2778e2)]/20 transition-all"
                      />
                    </div>
                    <button
                      v-if="formState.googleDriveLink"
                      type="button"
                      class="h-9 px-3 text-xs font-semibold rounded-xl flex items-center gap-1 cursor-pointer transition-all shrink-0 border"
                      :style="{
                        color: 'var(--color-primary, #2778e2)',
                        backgroundColor: 'rgba(var(--color-primary-rgb, 39, 120, 226), 0.1)',
                        borderColor: 'rgba(var(--color-primary-rgb, 39, 120, 226), 0.25)',
                      }"
                      title="Abrir link do Drive"
                      @click="openExternalLink(formState.googleDriveLink)"
                    >
                      <span class="i-lucide-external-link size-3.5" />
                      <span>Abrir</span>
                    </button>
                  </div>
                </div>

                <div class="sm:col-span-2">
                  <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                    Senha Meu INSS / GOV.BR
                  </label>
                  <div class="relative">
                    <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400 pointer-events-none">
                      <span class="i-lucide-key size-4" />
                    </span>
                    <input
                      v-model="formState.inssPassword"
                      type="text"
                      placeholder="Senha de acesso gov.br / Meu INSS"
                      class="w-full h-9 pl-9 pr-3 text-sm font-mono rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 outline-none focus:border-[var(--color-primary,#2778e2)] focus:ring-2 focus:ring-[var(--color-primary,#2778e2)]/20 transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- ABA 3: ATRIBUTOS PERSONALIZADOS DINÂMICOS -->
            <div v-show="activeTab === 'atributos'" class="space-y-3">
              <p class="text-xs text-slate-500 dark:text-slate-400 mb-2">
                Atributos personalizados definidos nas configurações da conta:
              </p>

              <div v-if="!allContactAttributes.length" class="p-6 text-center text-xs text-slate-400 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                Nenhum atributo personalizado cadastrado nesta conta.
              </div>

              <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div
                  v-for="attr in allContactAttributes"
                  :key="attr.id || attr.attributeKey"
                  class="flex flex-col gap-1"
                >
                  <label class="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">
                    {{ attr.attributeDisplayName || attr.attributeKey }}
                  </label>
                  
                  <!-- Checkbox boolean -->
                  <div v-if="attr.attributeDisplayType === 'checkbox'" class="flex items-center gap-2 h-9">
                    <input
                      v-model="formState.customAttributes[attr.attributeKey]"
                      type="checkbox"
                      class="size-4.5 rounded dark:bg-slate-900 dark:border-slate-700"
                      :style="{ accentColor: 'var(--color-primary, #2778e2)' }"
                    />
                    <span class="text-xs text-slate-600 dark:text-slate-400">
                      {{ formState.customAttributes[attr.attributeKey] ? 'Verdadeiro (Sim)' : 'Falso (Não)' }}
                    </span>
                  </div>

                  <!-- List / Select -->
                  <select
                    v-else-if="attr.attributeDisplayType === 'list' && attr.attributeValues?.length"
                    v-model="formState.customAttributes[attr.attributeKey]"
                    class="w-full h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 outline-none focus:border-[var(--color-primary,#2778e2)] cursor-pointer"
                  >
                    <option value="">Selecione...</option>
                    <option v-for="opt in attr.attributeValues" :key="opt" :value="opt">
                      {{ opt }}
                    </option>
                  </select>

                  <!-- Date -->
                  <input
                    v-else-if="attr.attributeDisplayType === 'date'"
                    v-model="formState.customAttributes[attr.attributeKey]"
                    type="date"
                    class="w-full h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 outline-none focus:border-[var(--color-primary,#2778e2)]"
                  />

                  <!-- Number -->
                  <input
                    v-else-if="attr.attributeDisplayType === 'number'"
                    v-model="formState.customAttributes[attr.attributeKey]"
                    type="number"
                    :placeholder="attr.attributeDescription || 'Valor numérico'"
                    class="w-full h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 outline-none focus:border-[var(--color-primary,#2778e2)]"
                  />

                  <!-- Standard Text / Link -->
                  <input
                    v-else
                    v-model="formState.customAttributes[attr.attributeKey]"
                    type="text"
                    :placeholder="attr.attributeDescription || 'Valor do atributo...'"
                    class="w-full h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 outline-none focus:border-[var(--color-primary,#2778e2)]"
                  />
                </div>
              </div>
            </div>

            <!-- ABA 4: PROCESSOS DO CLIENTE -->
            <div v-show="activeTab === 'processos'" class="space-y-3">
              <div
                v-for="(proc, index) in contactProcesses"
                :key="index"
                class="p-3.5 rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-800/50 space-y-2"
              >
                <div class="flex items-center justify-between gap-2">
                  <div class="flex items-center gap-2 min-w-0">
                    <span class="font-mono text-xs font-bold text-slate-900 dark:text-slate-100 truncate">
                      {{ proc.number || 'Processo sem número' }}
                    </span>
                    <span
                      v-if="proc.status"
                      class="px-2 py-0.5 text-[10px] font-semibold rounded-full shrink-0"
                      :class="getStatusBadgeClass(proc.status)"
                    >
                      {{ proc.status }}
                    </span>
                  </div>

                  <a
                    v-if="proc.url"
                    :href="proc.url"
                    target="_blank"
                    rel="noopener nofollow noreferrer"
                    class="text-xs hover:underline flex items-center gap-1 shrink-0 cursor-pointer"
                    :style="{ color: 'var(--color-primary, #2778e2)' }"
                  >
                    <span>Tribunal</span>
                    <span class="i-lucide-external-link size-3" />
                  </a>
                </div>

                <div class="grid grid-cols-2 gap-2 text-xs text-slate-500 dark:text-slate-400">
                  <div><span class="font-semibold text-slate-700 dark:text-slate-300">Tipo:</span> {{ proc.type || '-' }}</div>
                  <div><span class="font-semibold text-slate-700 dark:text-slate-300">Vara:</span> {{ proc.court || '-' }}</div>
                  <div class="col-span-2"><span class="font-semibold text-slate-700 dark:text-slate-300">Partes:</span> {{ proc.parties || '-' }}</div>
                </div>
              </div>
            </div>

          </div>

          <!-- Footer com Ações Glass -->
          <div class="flex items-center justify-between px-6 py-3.5 border-t border-slate-200/60 dark:border-slate-800/80 bg-white/40 dark:bg-slate-800/40">
            <a
              :href="contactProfileLink"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="text-xs font-semibold hover:underline flex items-center gap-1 cursor-pointer"
              :style="{ color: 'var(--color-primary, #2778e2)' }"
            >
              <span>Abrir tela cheia (/contacts)</span>
              <span class="i-lucide-external-link size-3" />
            </a>

            <div class="flex items-center gap-2.5">
              <button
                type="button"
                class="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
                :disabled="isSaving"
                @click="close"
              >
                Cancelar
              </button>

              <!-- Botão Salvar no tom base vibrante da paleta -->
              <button
                type="button"
                class="px-5 py-2 text-xs font-semibold text-white rounded-xl shadow-lg flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50 active:scale-[0.98] hover:brightness-110"
                :style="{
                  backgroundColor: 'var(--color-primary, #2778E2)',
                  boxShadow: '0 4px 14px 0 rgba(var(--color-primary-rgb, 39, 120, 226), 0.35)',
                }"
                :disabled="isSaving"
                @click="saveContact"
              >
                <span v-if="!isSaving" class="i-lucide-check size-4" />
                <span v-else class="i-lucide-loader-2 size-4 animate-spin" />
                <span>{{ isSaving ? 'Salvando...' : 'Salvar Alterações' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- LIGHTBOX / VISUALIZADOR DA FOTO EM ALTA RESOLUÇÃO -->
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 scale-90"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-90"
    >
      <div
        v-if="showImageLightbox"
        class="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xl"
        @click.self="closeImageLightbox"
        @keydown.esc="closeImageLightbox"
      >
        <div
          class="relative flex flex-col items-center max-w-lg w-full bg-slate-900/95 border border-slate-700/80 rounded-3xl p-6 shadow-2xl text-slate-100 overflow-hidden"
          @click.stop
        >
          <!-- Botão Fechar Lightbox -->
          <button
            type="button"
            class="absolute top-4 right-4 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors cursor-pointer z-10"
            title="Fechar (Esc)"
            @click="closeImageLightbox"
          >
            <span class="i-lucide-x size-5" />
          </button>

          <!-- Título com Nome do Contato -->
          <div class="text-center mb-4 pr-6 pl-2">
            <h3 class="text-base font-bold text-white truncate max-w-xs sm:max-w-sm">
              {{ contact.name || 'Foto do Contato' }}
            </h3>
            <p v-if="contact.phone_number" class="text-xs text-slate-400 font-mono mt-0.5">
              {{ contact.phone_number }}
            </p>
          </div>

          <!-- Imagem Ampliada -->
          <div class="relative w-64 h-64 sm:w-80 sm:h-80 rounded-2xl overflow-hidden shadow-2xl border-2 border-slate-700/80 bg-slate-950 flex items-center justify-center">
            <img
              v-if="contact.thumbnail"
              :src="contact.thumbnail"
              :alt="contact.name"
              class="w-full h-full object-cover select-none"
            />
            <div v-else class="flex flex-col items-center justify-center text-slate-500 gap-2 p-4 text-center">
              <Avatar
                :name="contact.name"
                :size="96"
                hide-offline-status
                class="!rounded-3xl shadow-lg"
              />
              <span class="text-xs text-slate-400 mt-2">Sem foto personalizada disponível</span>
            </div>
          </div>

          <!-- Ações do Lightbox -->
          <div class="flex items-center gap-3 mt-5">
            <a
              v-if="contact.thumbnail"
              :href="contact.thumbnail"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-xl transition-all cursor-pointer shadow-md hover:brightness-110"
              :style="{ backgroundColor: 'var(--color-primary, #2778E2)' }"
            >
              <span class="i-lucide-external-link size-3.5" />
              <span>Abrir Original em Nova Aba</span>
            </a>

            <button
              type="button"
              class="px-4 py-2 text-xs font-semibold text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-xl transition-colors cursor-pointer"
              @click="closeImageLightbox"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>
